import React from 'react';
import PropTypes from 'prop-types';
import { find } from 'lodash';

import View from 'components/view';
import Modal from 'components/modal';
import Button from 'components/button';
import KebabButton from 'components/button/kebab';
import CollectionEmpty from 'components/collection/empty';
import Tooltip from 'components/tooltip/tooltip';
import Help from 'components/tooltip/help';
import Status from 'components/status';

import EntriModal from './entri-modal';

export default class StorefrontDomains extends React.PureComponent {
  static contextTypes = {
    client: PropTypes.object.isRequired,
    openModal: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      showingCheck: false,
      showingConnect: false,
      domain: null,
      checking: false,
    };
  }

  onClickConnect = async (event) => {
    event.preventDefault();
    this.setState({ showingConnect: true });
  };

  onEntriClose = (event) => {
    const { domains, onCreateDomain, onUpdateDomain } = this.props;
    const { showingCheck } = this.state;
    const { detail } = event;

    this.setState({ showingConnect: false });

    const finished = [
      'FINISHED_SUCCESSFULLY',
      'FINISHED_SUCCESSFULLY_MANUAL',
    ].includes(detail.lastStatus);

    if (finished && detail.domain && detail.success) {
      const domainName = this.getFullDomainName(detail.domain);
      const domain = find(domains.results, { name: domainName });

      // Update or create domain
      if (domain) {
        onUpdateDomain(domain.id, {
          entri_detail: detail,
        });
      } else {
        onCreateDomain({
          name: domainName,
          entri_detail: detail,
        });
      }

      if (showingCheck) {
        this.checkStatus();
      } else {
        this.props.fetchDomains();
      }
    }
  };

  getFullDomainName(domain) {
    // If not using subdomain.domain.tld, add www
    if (String(domain).split('.').length === 2) {
      return `www.${domain}`;
    }
    return domain;
  }

  onClickCheckConnection = (event) => {
    event.preventDefault();

    const { domains } = this.props;
    const domainId = event.currentTarget.dataset.id;
    const domain = find(domains.results, { id: domainId });

    if (domain) {
      this.setState({
        showingCheck: true,
        domain,
      });
    }
  };

  onCloseCheck = () => {
    this.setState({ showingCheck: false, domain: null });
  };

  onClickCheckAction = async (event) => {
    event.preventDefault();
    this.checkStatus();
  };

  async checkStatus() {
    const { onVerifyDomain } = this.props;
    const { domain } = this.state;

    this.setState({ checking: true });

    const domainChecked = await onVerifyDomain(domain.id);

    this.setState({ domain: domainChecked, checking: false });
  }

  onClickMakePrimary = (event) => {
    event.preventDefault();
    const { domains } = this.props;
    const domainId = event.currentTarget.dataset.id;
    const domain = find(domains.results, { id: domainId });

    this.context.openModal('Confirm', {
      title: `Make domain primary`,
      message: (
        <p>
          Your online store use this domain by default:{' '}
          <code>{domain.name}</code>
        </p>
      ),
      action: 'Confirm',
      actionType: 'default',
      onConfirm: async () => {
        await this.props.onUpdateDomain(domainId, { primary: true });
      },
    });
  };

  onClickRemove = (event) => {
    event.preventDefault();
    const { domains } = this.props;
    const domainId = event.currentTarget.dataset.id;
    const domain = find(domains.results, { id: domainId });

    this.context.openModal('Confirm', {
      title: `Remove domain`,
      message: (
        <p>
          Are you sure you want to remove domain <code>{domain.name}</code>?
        </p>
      ),
      action: 'Remove',
      actionType: 'danger',
      onConfirm: async () => {
        await this.props.onDeleteDomain(domainId);
      },
    });
  };

  renderCheckConnectionModal() {
    const { domain, checking } = this.state;

    return (
      <Modal
        title="Check domain connection"
        className="storefront-domains-verify-modal"
        width={600}
        actions={[
          {
            label: 'Refresh',
            type: 'secondary',
            onClick: this.onClickCheckAction,
          },
        ]}
        loading={checking}
        onClose={this.onCloseCheck}
        cancelText="Close"
        tapout={false}
      >
        <>
          <p>
            <code>{domain.name}</code>
          </p>
          {domain.verifying ? (
            !domain.connected ? (
              <p>
                Your domain connection is being verified. This can take up to 24
                hours once your DNS record is configured correctly. Click below
                to modify your configuration, or refresh status.
              </p>
            ) : (
              <p>
                Your domain is connected and an SSL certificate is being issued
                automatically. Click below to modify your configuration, or
                refresh status.
              </p>
            )
          ) : (
            <p>
              Your domain has been successfully connected. Click below to modify
              your configuration, or refresh status.
            </p>
          )}
          <Button onClick={this.onClickConnect} type="secondary" size="sm">
            Configure domain
          </Button>
        </>
      </Modal>
    );
  }

  renderDomainStatus(domain) {
    if (!domain.verified) {
      if (!domain.connected) {
        if (domain.verifying) {
          return (
            <>
              <Tooltip message="This can take up to 24 hours">
                <Status type="muted" loading={true}>
                  Verifying connection
                </Status>
              </Tooltip>
            </>
          );
        } else {
          return (
            <>
              <Tooltip message={domain.verify_error}>
                <Status type="muted">Unable to verify connection</Status>
              </Tooltip>
            </>
          );
        }
      }

      if (domain.ssl_status === 'issuing') {
        if (domain.verifying) {
          return (
            <>
              <Tooltip message="This can take up to 24 hours">
                <Status type="muted" loading={true}>
                  Issuing SSL certificate
                </Status>
              </Tooltip>
            </>
          );
        } else {
          return (
            <>
              <Tooltip message={domain.verify_error}>
                <Status type="warning">Unable to issue SSL certificate</Status>
              </Tooltip>
            </>
          );
        }
      }
    }

    return <Status type="positive">Connected</Status>;
  }

  renderActions(domain) {
    return (
      <KebabButton
        side="bottom"
        items={[
          !domain.primary && (
            <button
              key="primary"
              onClick={this.onClickMakePrimary}
              data-id={domain.id}
              disabled={!domain.verified}
              className={!domain.verified ? 'disabled' : ''}
            >
              Make primary
            </button>
          ),
          <button
            key="check"
            onClick={this.onClickCheckConnection}
            data-id={domain.id}
          >
            Refresh status
          </button>,
          <hr key="hr-delete" />,
          <button
            key="remove"
            className="danger"
            onClick={this.onClickRemove}
            data-id={domain.id}
          >
            Delete domain
          </button>,
        ]}
      />
    );
  }

  render() {
    const { domains } = this.props;
    const { showingCheck, showingConnect, domain } = this.state;

    // make primary always first
    const domainsSorted = domains.results.sort((a, b) => {
      if (a.primary) {
        return -1;
      }

      if (b.primary) {
        return 1;
      }

      return 0;
    });

    return (
      <>
        <View
          key="domains"
          detail={true}
          headerTitle="Domains"
          headerSubtitle="Manage custom domains linked to your hosted storefront and checkout"
          headerActions={[
            domains.count > 0 && {
              label: 'Connect domain',
              onClick: this.onClickConnect,
            },
          ]}
        >
          {domains.count > 0 ? (
            <table className="collection-table outer">
              <thead>
                <tr>
                  <th width="30%">Domain</th>
                  <th>Status</th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {domainsSorted.map((domain) => (
                  <tr key={domain.id}>
                    <td width="20%">
                      <code className="nowrap">{domain.name}</code>
                      {domain.primary ? (
                        <>
                          &ensp;
                          <Status type="muted" dot={false}>
                            Primary
                          </Status>
                        </>
                      ) : (
                        <Help message="This will redirect to your primary domain" />
                      )}
                    </td>
                    <td>{this.renderDomainStatus(domain)}</td>
                    <td className="action">{this.renderActions(domain)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <CollectionEmpty
              emptyTitle="Customize your domain"
              emptyDescription="Add a custom domain to represent your storefront"
              emptyAction="Connect domain"
              onClickAction={this.onClickConnect}
            />
          )}
        </View>
        {showingCheck && this.renderCheckConnectionModal()}
        {showingConnect && (
          <EntriModal
            onClose={this.onEntriClose}
            fetchEntriToken={this.props.fetchEntriToken}
            domain={domain}
          />
        )}
      </>
    );
  }
}
